import { UploadSquare } from 'iconoir-react'
import { startCase } from 'lodash'
import { ReactElement } from 'react'
import { useQuery } from 'urql'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { IMPORT_DETAIL_QUERY } from 'app/components/imports/graphql'
import type { ActivityJson } from 'app/types/entities'

type Props = {
  activity: ActivityJson
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

/**
 * Renders an import activity that is not be Chat, Online Scheduler, or Virtual Assistant.
 * @Prop activity - Activity to query the Form from.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @returns ReactElement
 */
export function CustomActivityTypeCard({ activity, isFirst, isLast, alwaysOpen }: Props): ReactElement {
  const [{ data }] = useQuery({
    query: IMPORT_DETAIL_QUERY,
    variables: { id: activity.typeId ?? `` },
    pause: !activity.typeId,
  })
  const customActivity = activity.typeId ? data?.importDetail : undefined
  return (
    <ActivityCard
      title={`${startCase(activity?.type)} Import`}
      activityDate={activity?.activityDate}
      icon={<UploadSquare className="h-5 w-5 rounded-md bg-green-600 p-1 text-on-color" />}
      drawer={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {customActivity && (
            <KeyValueDrawer
              map={{
                'Activity Vendor': customActivity.vendor,
                Name: customActivity.name,
                'Requested Appointment Date': formatDate({
                  value: customActivity.requestedAppointmentTime,
                  dateOnly: true,
                }),
                'Phone Number': formatPhoneNumber(customActivity.phone),
                Email: customActivity.email,
                Scheduled: customActivity.isScheduled ? `Yes` : `No`,
                'Requested Treatment': customActivity.treatment,
                Channel: customActivity.channel,
                Staff: customActivity.staff,
              }}
            />
          )}
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
